import type { MouseEvent } from 'react';

import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import { useOptionalScoreUnit, useScoreUnit } from '../ScoreUnitContext';
import type { ScoreUnit } from '../types';

export const SCORE_UNIT_OPTIONS: ReadonlyArray<{
  value: ScoreUnit;
  label: string;
}> = [
  { value: 'proficiency_level', label: 'Proficiency level' },
  { value: 'scale_score', label: 'Scale score' },
];

export function makeScoreUnitChangeHandler(setScoreUnit: (unit: ScoreUnit) => void) {
  return (_event: MouseEvent<HTMLElement>, next: ScoreUnit | null) => {
    if (next !== null) {
      setScoreUnit(next);
    }
  };
}

export function ScoreUnitToggle() {
  const { scoreUnit, setScoreUnit } = useScoreUnit();

  return (
    <ToggleButtonGroup
      exclusive
      value={scoreUnit}
      onChange={makeScoreUnitChangeHandler(setScoreUnit)}
      aria-label="Score units"
    >
      {SCORE_UNIT_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export function ScoreUnitSegment({
  unit,
  onChange,
}: {
  unit: ScoreUnit;
  onChange?: (unit: ScoreUnit) => void;
}) {
  const ctx = useOptionalScoreUnit();
  const setUnit = onChange ?? ctx?.setScoreUnit ?? (() => undefined);
  return (
    <Box
      data-testid="score-unit-toggle"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
    >
      <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>
        Score units
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={unit}
        onChange={makeScoreUnitChangeHandler(setUnit)}
        aria-label="Score units"
      >
        {SCORE_UNIT_OPTIONS.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
